import { getAnalytics, logEvent } from '@firebase/analytics'
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  type RemoteConfig,
} from 'firebase/remote-config'

export const sendEvent = (eventName: string, params?: any) => {
  const analytics = getAnalytics()

  if (analytics) {
    logEvent(analytics, eventName, params)
  }
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
}

const app = initializeApp(firebaseConfig)
const analytics = getAnalytics(app)

/** Chave no Firebase Remote Config (tipo Boolean no console). Esconde aba Chat na análise do prontuário. */
export const REMOTE_CONFIG_MEDICAL_RECORD_CHAT_ENABLED =
  'medical_record_chat_enabled'

/** Chave no Firebase Remote Config (tipo Boolean). Exibe botão de gravar áudio no chat. */
export const REMOTE_CONFIG_CHAT_AUDIO_RECORD_ENABLED =
  'chat_audio_record_enabled'

/** Fase 2: lê o `/history` em paralelo ao SDK só para medir divergência. */
export const REMOTE_CONFIG_CHAT_HISTORY_SHADOW_READ = 'chat_history_shadow_read'
/**
 * Fase 3: troca a fonte de leitura do histórico do SDK para o `/history`.
 *
 * Desligada por padrão e assim deve permanecer até o portão da Fase 2 fechar
 * com dado — ligar isto sem paridade medida arrisca o histórico clínico.
 */
export const REMOTE_CONFIG_CHAT_HISTORY_AS_SOURCE = 'chat_history_as_source'
/**
 * Fase 4: presença gerida pelo backend (`presence/ensure`).
 *
 * Só tem efeito com a Fase 3 ligada — ver `useChatPresenceEnsure`. Sem o
 * histórico vindo da API, perder a participação deixaria o profissional sem
 * leitura de dado clínico.
 */
export const REMOTE_CONFIG_CHAT_PRESENCE_ENSURE = 'chat_presence_ensure'

/** Fase 5 — Passos 1–3 (defensivos): ler `closedAt`, modo encerrado, 409. */
export const REMOTE_CONFIG_CONVERSATION_CLOSE_READ = 'conversation_close_read'
/** Fase 5 — Passo 4: reabertura silenciosa de `closedBy: SYSTEM`. */
export const REMOTE_CONFIG_CONVERSATION_AUTO_REOPEN = 'conversation_auto_reopen'
/** Fase 5 — Passos 5–6: encerrar ao finalizar/cancelar e reabrir explícito. */
export const REMOTE_CONFIG_CONVERSATION_CLOSE_ACTION =
  'conversation_close_action'

let remoteConfigSingleton: RemoteConfig | null = null

export function getFirebaseRemoteConfig(
  appInstance: FirebaseApp
): RemoteConfig {
  if (!remoteConfigSingleton) {
    remoteConfigSingleton = getRemoteConfig(appInstance)
    remoteConfigSingleton.settings.minimumFetchIntervalMillis =
      process.env.NODE_ENV === 'production' ? 5 * 60 * 1000 : 0
    remoteConfigSingleton.defaultConfig = {
      [REMOTE_CONFIG_MEDICAL_RECORD_CHAT_ENABLED]: true,
      [REMOTE_CONFIG_CHAT_AUDIO_RECORD_ENABLED]: true,
      // Desligado por padrão: é medição em segundo plano, ligada por ambiente.
      [REMOTE_CONFIG_CHAT_HISTORY_SHADOW_READ]: false,
      [REMOTE_CONFIG_CHAT_HISTORY_AS_SOURCE]: false,
      [REMOTE_CONFIG_CHAT_PRESENCE_ENSURE]: false,
      [REMOTE_CONFIG_CONVERSATION_CLOSE_READ]: false,
      [REMOTE_CONFIG_CONVERSATION_AUTO_REOPEN]: false,
      [REMOTE_CONFIG_CONVERSATION_CLOSE_ACTION]: false,
    }
  }
  return remoteConfigSingleton
}

export const remoteConfig = getFirebaseRemoteConfig(app)

let fetchRemoteConfigPromise: Promise<boolean> | null = null

/** Busca e ativa valores remotos (com fallback para defaultConfig). */
export function fetchAndActivateRemoteConfig(): Promise<boolean> {
  if (!fetchRemoteConfigPromise) {
    fetchRemoteConfigPromise = fetchAndActivate(remoteConfig).catch((err) => {
      fetchRemoteConfigPromise = null
      console.warn('[Firebase Remote Config]', err)
      return false
    })
  }
  return fetchRemoteConfigPromise
}

export function getMedicalRecordChatEnabledFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_MEDICAL_RECORD_CHAT_ENABLED
  ).asBoolean()
}

export function getChatAudioRecordEnabledFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CHAT_AUDIO_RECORD_ENABLED
  ).asBoolean()
}

export function getChatHistoryShadowReadFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CHAT_HISTORY_SHADOW_READ
  ).asBoolean()
}

export function getChatHistoryAsSourceFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CHAT_HISTORY_AS_SOURCE
  ).asBoolean()
}

export function getChatPresenceEnsureFromRemote(): boolean {
  return getValue(remoteConfig, REMOTE_CONFIG_CHAT_PRESENCE_ENSURE).asBoolean()
}

export function getConversationCloseReadFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CONVERSATION_CLOSE_READ
  ).asBoolean()
}

export function getConversationAutoReopenFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CONVERSATION_AUTO_REOPEN
  ).asBoolean()
}

export function getConversationCloseActionFromRemote(): boolean {
  return getValue(
    remoteConfig,
    REMOTE_CONFIG_CONVERSATION_CLOSE_ACTION
  ).asBoolean()
}

export { analytics }
